/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import commentDataService from "services/comment";

// --- Initial State ---
const initialState = {
  comments: [],
  transformedComments: {},
  profileComments: {
    items: [],
    totalCount: 0,
    transformedComments: {},
  },
};

// --- Async Thunks ---
export const getComments = createAsyncThunk(
  "comments/get-comments",
  async (
    { companyId, organizationId, createdBy, officialIds, corporate },
    thunkAPI
  ) => {
    try {
      const res = await commentDataService.getAll(
        companyId,
        organizationId,
        createdBy,
        officialIds,
        corporate
      );
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const addComment = createAsyncThunk(
  "comments/add-comment",
  async ({ data }, thunkAPI) => {
    try {
      const res = await commentDataService.create(data);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const editComment = createAsyncThunk(
  "comments/update",
  async ({ commentId, data }, thunkAPI) => {
    try {
      const res = await commentDataService.update(commentId, data);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const deleteComment = createAsyncThunk(
  "comments/delete",
  async ({ commentId }, thunkAPI) => {
    try {
      await commentDataService.remove(commentId);
      return { commentId };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const getProfileComments = createAsyncThunk(
  "comments/getProfileComments",
  async ({ userId }, thunkAPI) => {
    try {
      const res = await commentDataService.getProfileComments(userId);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

// --- Slice ---
const commentsSlice = createSlice({
  name: "Comments",
  initialState,
  reducers: {
    resetCommentsSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getComments.fulfilled, (state, action) => {
        state.comments = action.payload.items;
        state.transformedComments = action.payload.transformedComments;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        if (action?.meta?.arg?.isProfileReview) {
          state.profileComments.items.push(action.payload.comment);
        } else {
          state.comments.push(action.payload.comment);
        }
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { payload } = action;
        if (action?.meta?.arg?.isProfileReview) {
          const index = state.profileComments.items.findIndex(
            (comment) => payload._id === comment._id
          );
          if (index > -1) {
            state.profileComments.items.splice(index, 1, payload);
          }
        } else {
          const index = state.comments.findIndex(
            (comment) => payload._id === comment._id
          );
          if (index > -1) {
            state.comments.splice(index, 1, payload);
          }
        }
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { payload } = action;
        if (action?.meta?.arg?.isProfileReview) {
          const index = state.profileComments.items.findIndex(
            (comment) => payload.commentId === comment._id
          );
          if (index > -1) state.profileComments.items.splice(index, 1);
        } else {
          const index = state.comments.findIndex(
            (comment) => payload.commentId === comment._id
          );
          if (index > -1) state.comments.splice(index, 1);
        }
      })
      .addCase(getProfileComments.fulfilled, (state, { payload }) => {
        state.profileComments = payload;
      });
  },
});

// --- Exports ---
export const { resetCommentsSlice } = commentsSlice.actions;
export default commentsSlice.reducer;
